import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, HelpCircle, X } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import FAQItem from '../../components/support/FAQItem';
import ErrorState from '../../components/common/ErrorState';
import { ListSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../context/ToastContext';
import { fetchFAQs } from '../../utils/api';
import { SUPPORT_CATEGORIES } from '../../utils/constants';

/**
 * FAQPage - 자주 묻는 질문 페이지
 */
const FAQPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [expandAll, setExpandAll] = useState(false);

  // FAQ/문의 공용 카테고리 통일 (PAGE_IMPROVEMENTS 4번)
  const categories = useMemo(() => ['전체', ...SUPPORT_CATEGORIES], []);

  const loadFaqs = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchFAQs();
      setFaqs(data || []);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
      setIsError(true);
      showToast('FAQ를 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  // 검색어 디바운스
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredFaqs = useMemo(() => {
    let filtered = faqs;

    if (selectedCategory !== '전체') {
      filtered = filtered.filter((faq) => faq.category === selectedCategory);
    }

    if (debouncedTerm.trim()) {
      const term = debouncedTerm.toLowerCase();
      filtered = filtered.filter(
        (faq) =>
          faq.question.toLowerCase().includes(term) ||
          faq.answer.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [faqs, selectedCategory, debouncedTerm]);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <HelpCircle size={32} className="text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">
            자주 묻는 질문
          </h1>
          <p className="text-sm text-accent-600">
            궁금하신 내용을 검색하거나 카테고리를 선택해보세요
          </p>
        </div>

        {/* 검색 */}
        <div className="mb-6">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-400"
              size={20}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="질문 검색..."
              aria-label="FAQ 검색"
              className="w-full pl-10 pr-10 py-3 rounded-lg border border-accent-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                aria-label="검색어 지우기"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-400 hover:text-primary"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* 카테고리 필터 */}
        <div className="mb-4 flex gap-2 flex-wrap">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              aria-pressed={selectedCategory === category}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* 전체 펼침/접기 */}
        {!isLoading && !isError && filteredFaqs.length > 0 && (
          <div className="mb-3 flex justify-end">
            <button
              onClick={() => setExpandAll((v) => !v)}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {expandAll ? '전체 접기' : '전체 펼치기'}
            </button>
          </div>
        )}

        {/* FAQ 목록 */}
        {isLoading ? (
          <ListSkeleton rows={5} />
        ) : isError ? (
          <ErrorState
            title="FAQ를 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadFaqs}
          />
        ) : filteredFaqs.length > 0 ? (
          <div className="space-y-3" aria-live="polite">
            {filteredFaqs.map((faq, index) => (
              <FAQItem
                key={faq.id}
                faq={faq}
                searchTerm={debouncedTerm}
                defaultOpen={
                  expandAll || (index === 0 && !debouncedTerm)
                }
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-accent-50 rounded-lg">
            <p className="text-accent-600">
              {debouncedTerm || selectedCategory !== '전체'
                ? '검색 결과가 없습니다.'
                : '등록된 FAQ가 없습니다.'}
            </p>
          </div>
        )}

        {/* 추가 안내 */}
        {!isLoading && !isError && (
          <div className="mt-8 p-4 bg-secondary-50 rounded-lg border border-accent-200">
            <h3 className="font-semibold text-primary-900 mb-2">
              답변을 찾지 못하셨나요?
            </h3>
            <p className="text-sm text-accent-600 mb-3">
              1:1 문의를 통해 궁금하신 내용을 문의해주시면 빠르게
              답변드리겠습니다.
            </p>
            <button
              onClick={() => navigate('/support/inquiry')}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
            >
              1:1 문의하기 →
            </button>
          </div>
        )}

        {/* 통계 정보 */}
        {!isLoading && !isError && filteredFaqs.length > 0 && (
          <div className="mt-6 text-center text-sm text-accent-600">
            총 {filteredFaqs.length}개의 FAQ
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default FAQPage;
